/**
 * 財務諸表等電子開示（WAM NET 用CSV出力）
 * 社会福祉法人の財務諸表等電子開示システム（福祉医療機構 WAM）で利用可能なCSVを出力します。
 */
import React, { useState } from 'react';

interface ElectronicDisclosureProps {
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  selectedYear?: number;
  onBack?: () => void;
}

const ElectronicDisclosure: React.FC<ElectronicDisclosureProps> = ({
  apiFetch,
  selectedYear = new Date().getFullYear(),
  onBack,
}) => {
  const [periodStart, setPeriodStart] = useState(`${selectedYear}-04-01`);
  const [periodEnd, setPeriodEnd] = useState(`${selectedYear + 1}-03-31`);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async (type: 'cf' | 'pl' | 'bs', corporate = false) => {
    setError(null);
    setLoading(corporate ? `${type}-corporate` : type);
    try {
      const base = '/api/reports/wam-net';
      const path = corporate ? `${type}/corporate` : type;
      const params = type === 'bs'
        ? `period_end=${encodeURIComponent(periodEnd)}`
        : `period_start=${encodeURIComponent(periodStart)}&period_end=${encodeURIComponent(periodEnd)}`;
      const url = `${base}/${path}?${params}`;
      const response = await apiFetch(url);
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `ダウンロードに失敗しました (${response.status})`);
      }
      const blob = await response.blob();
      const disp = response.headers.get('Content-Disposition');
      const match = disp && /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disp);
      const filename = match ? match[1].replace(/['"]/g, '') : `WAM_${corporate ? 'CORPORATE_' : ''}${type.toUpperCase()}_${periodEnd.replace(/-/g, '')}.csv`;
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(urlObj);
      a.remove();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ダウンロードに失敗しました');
    } finally {
      setLoading(null);
    }
  };

  const endDateOnly = periodEnd;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-gray-800">財務諸表等電子開示（WAM NET用CSV）</h1>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 bg-white text-sm"
          >
            メニューに戻る
          </button>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-gray-700">
        <p className="font-medium text-blue-900 mb-1">社会福祉法人の財務諸表等電子開示システムについて</p>
        <p>
          福祉医療機構（WAM）が運営する「社会福祉法人の財務諸表等電子開示システム」に届け出る際に利用できる、
          計算書類用のCSVデータをダウンロードできます。資金収支計算書・事業活動計算書・貸借対照表の各CSVを出力し、
          必要に応じてWAM NETの操作説明に従ってご利用ください。
        </p>
      </div>

      <div className="bg-white border rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-medium text-gray-900">対象期間</h2>
        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2">
            <span className="text-sm text-gray-600">開始日</span>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-sm text-gray-600">終了日</span>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
            {error}
          </div>
        )}

        <h2 className="text-lg font-medium text-gray-900 pt-2">CSVダウンロード</h2>
        <p className="text-sm text-gray-600">
          各ボタンで、上記期間のデータをWAM NET用CSV形式でダウンロードします。
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => handleDownload('cf')}
            disabled={!!loading}
            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
          >
            {loading === 'cf' ? '出力中...' : '資金収支計算書 (CF) CSV'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('pl')}
            disabled={!!loading}
            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
          >
            {loading === 'pl' ? '出力中...' : '事業活動計算書 (PL) CSV'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('bs')}
            disabled={!!loading}
            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
          >
            {loading === 'bs' ? '出力中...' : '貸借対照表 (BS) CSV'}
          </button>
        </div>

        <h2 className="text-lg font-medium text-gray-900 pt-4">合算法人拠点 CSVダウンロード</h2>
        <p className="text-sm text-gray-600">
          法人配下の全施設（法人本部・保育所等）のデータを1ファイルにまとめて出力します。
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => handleDownload('cf', true)}
            disabled={!!loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 text-sm"
          >
            {loading === 'cf-corporate' ? '出力中...' : '合算 CF CSV'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('pl', true)}
            disabled={!!loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 text-sm"
          >
            {loading === 'pl-corporate' ? '出力中...' : '合算 PL CSV'}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('bs', true)}
            disabled={!!loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 text-sm"
          >
            {loading === 'bs-corporate' ? '出力中...' : '合算 BS CSV'}
          </button>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        <p>※ XBRL形式は上場企業向けEDINETで利用されています。社会福祉法人はWAMの電子開示システム（CSV）をご利用ください。</p>
      </div>
    </div>
  );
};

export default ElectronicDisclosure;
